import { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useIntl, navigate } from 'gatsby-plugin-intl';
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';

import Link from '../Link';
import InputField from '../InputField';
import RichText, { plainText } from '../RichText';

import {
  FormContainer,
  Form,
  SearchContainer,
  PredictiveSearchContainer,
} from './styles';

const withFaqSearch = (Search) => {
  const FaqSearch = ({ className, data, themes }) => {
    const intl = useIntl();

    const { id, searchBarLabel } = data;

    const [focus, setFocus] = useState(false);
    const [query, setQuery] = useState(``);
    const [results, setResults] = useState([]);

    const formSubmit = (e, search = query) => {
      e.preventDefault();
      if (search.length < 1) return;
      navigate(`/faq/search-results?searchQuery=${search}`);
    };

    const focusOutsideOfSearch = useCallback((event) => {
      const { keyCode } = event;
      if (keyCode === 9) {
        if (event.target.classList.contains('searchable-marker')) {
          return;
        }
        setFocus(false);
      }
    }, []);

    const clickOutsideOfSearch = useCallback((event) => {
      if (event.target.classList.contains('searchable-marker')) {
        return;
      }
      setFocus(false);
    }, []);

    const handleSearch = (e) => {
      if (e.key === 'Escape') setQuery('');
      setQuery(e.target.value.replace(/[&/\\#,+()$~%.'":*\-^<>{}]/g, ''));
    };

    useEffect(() => {
      if (window) {
        window.addEventListener('keyup', focusOutsideOfSearch);
        window.addEventListener('click', clickOutsideOfSearch);
      }
      return () => {
        window.removeEventListener('keyup', focusOutsideOfSearch);
        window.removeEventListener('click', clickOutsideOfSearch);
      };
    }, [focusOutsideOfSearch]);

    useEffect(() => {
      if (!query || !window.__LUNR__) {
        setResults([]);
        return;
      }
      const lunrIndex =
        intl.locale === 'en' ? window.__LUNR__.en : window.__LUNR__.fr;
      const searchResults = lunrIndex.index.search(query.replace('?', ''));
      const filteredResults = searchResults
        .map(({ ref }) => {
          return lunrIndex.store[ref];
        })
        .filter((_, index) => index <= 3);
      setResults(filteredResults);
    }, [query]);

    return (
      <FormContainer>
        <Form className={className} onSubmit={formSubmit}>
          <SearchContainer>
            <Search
              id={id}
              type="search"
              label={searchBarLabel}
              value={query}
              onFocus={() => setFocus(true)}
              onChange={handleSearch}
              active={focus}
              themes={themes}
            >
              {query && (
                <p className="accessibility" role="status" aria-live="polite">
                  {query}. {results.length} FAQ{' '}
                  {intl.formatMessage({ id: 'ResultsFound' })}
                </p>
              )}
              {focus && (
                <PredictiveSearchContainer
                  className="searchable-marker"
                  themes={themes}
                >
                  {results.map(({ id: key, page }) => {
                    const searchResultsQuery = documentToPlainTextString(
                      JSON.parse(page.raw)
                    );
                    return (
                      <li key={key}>
                        <Link
                          className="searchable-marker"
                          to={`/faq/search-results?searchQuery=${searchResultsQuery}`}
                          state={{ richTextQuery: page }}
                        >
                          <RichText document={page} options={plainText} />
                        </Link>
                      </li>
                    );
                  })}
                </PredictiveSearchContainer>
              )}
            </Search>
          </SearchContainer>
        </Form>
      </FormContainer>
    );
  };
  FaqSearch.propTypes = {
    className: PropTypes.string,
    themes: PropTypes.string.isRequired,
    data: PropTypes.shape({
      id: PropTypes.string,
      searchBarLabel: PropTypes.string,
    }).isRequired,
  };
  FaqSearch.defaultProps = {
    className: null,
  };
  return FaqSearch;
};

export default withFaqSearch(InputField);
